/* Quick View drawer.
   - Triggered by any element with class "js-quick-view-trigger" and a
     "data-product-url" attribute (added in snippets/product-card.liquid).
   - Fetches the clicked product's public JSON (`{url}.js`) client-side --
     this theme currently ships no product template/section, so there is
     nothing to render server-side via the Section Rendering API. All
     gallery / price / variant swatch logic below runs entirely in JS
     against that JSON payload.
   - Reuses global.js's trapFocus / removeTrapFocus / formatMoney helpers
     (loaded non-deferred in <head>, so they're guaranteed ready before
     this deferred script runs) and the same open/close class pattern as
     CartDrawer (assets/cart-drawer.js) for consistency. */
(function () {
  class QuickViewDrawer extends HTMLElement {
    constructor() {
      super();
      this.overlay = this.querySelector('#QuickViewDrawer-Overlay');
      this.closeButton = this.querySelector('.quick-view-drawer__close');
      this.content = this.querySelector('#QuickViewDrawer-Content');
      this.openedBy = null;
      this.product = null;
      this.swiper = null;

      this.addEventListener('keyup', (evt) => {
        if (evt.code === 'Escape') this.close();
      });
      this.overlay && this.overlay.addEventListener('click', this.close.bind(this));
      this.closeButton && this.closeButton.addEventListener('click', this.close.bind(this));
    }

    open(triggeredBy) {
      this.openedBy = triggeredBy || null;
      setTimeout(() => this.classList.add('active'));
      document.body.classList.add('overflow-hidden');
      const inner = this.querySelector('.drawer__inner');
      if (inner) {
        trapFocus(inner);
        inner.focus();
      }
    }

    close() {
      this.classList.remove('active');
      removeTrapFocus(this.openedBy);
      document.body.classList.remove('overflow-hidden');
      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }
    }

    renderLoading() {
      this.content.innerHTML =
        '<div class="quick-view-drawer__loading"><div class="loading-overlay__spinner"><svg aria-hidden="true" focusable="false" role="presentation" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg"><circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle></svg></div></div>';
      const headerTitle = this.querySelector('#QuickViewDrawer-Title');
      if (headerTitle) headerTitle.textContent = '';
    }

    renderError() {
      this.content.innerHTML =
        '<div class="quick-view-drawer__error">' + (window.quickViewStrings && window.quickViewStrings.error ? window.quickViewStrings.error : 'Sorry, this product could not be loaded.') + '</div>';
    }

    money(cents) {
      return typeof formatMoney === 'function' ? formatMoney(cents, window.themeMoneyFormat) : '$' + (cents / 100).toFixed(2);
    }

    optionNames(product) {
      // product.js's "options" field has shipped as two different shapes
      // across Shopify versions/stores: a plain array of name strings
      // (["Color","Size"]) on some, or an array of {name,position,values}
      // objects on others (confirmed live on this store: options: [{name:
      // "Renk", ...}, {name:"Beden", ...}]). Some stores additionally (or
      // instead) expose "options_with_values" with the same object shape.
      // Normalize all of these down to a plain array of name strings.
      const source = (product.options_with_values && product.options_with_values.length ? product.options_with_values : product.options) || [];
      return source.map((opt) => (typeof opt === 'string' ? opt : opt && opt.name));
    }

    findOptionIndex(product, names) {
      return this.optionNames(product).findIndex((name) => names.includes(String(name || '').toLowerCase()));
    }

    findColorOptionIndex(product) {
      // "Renk" = Turkish for "Color" -- this store's product data uses
      // Turkish option names.
      return this.findOptionIndex(product, ['color', 'colour', 'renk']);
    }

    findSizeOptionIndex(product) {
      // "Beden" / "Boyut" = Turkish for "Size" (different merchants/products
      // in this catalog use either word).
      return this.findOptionIndex(product, ['size', 'beden', 'boyut']);
    }

    normalizeSrc(src) {
      // product.images entries come back protocol-relative ("//cdn...")
      // while variant.featured_image.src is absolute ("https://cdn...") --
      // strip the protocol from both sides before comparing so a variant's
      // cover photo can be matched against the gallery's image list.
      return (src || '').replace(/^https?:/, '').split('?')[0];
    }

    // Generic replacement for the old color-only matcher: "overrides" is a
    // map of option-index -> forced value (e.g. { 0: 'Black' } when the
    // Color option is index 0); every other option keeps whatever value
    // "currentVariant" already has, where possible. Falls back to a looser
    // match (ignoring currentVariant entirely) if no exact combo exists --
    // e.g. the previously-selected size doesn't exist in the newly-picked
    // color -- so a click always lands on *some* real variant.
    findVariantForOptions(product, overrides, currentVariant) {
      const numOptions = product.options.length;
      const exact = product.variants.find((v) => {
        for (let i = 0; i < numOptions; i++) {
          const key = 'option' + (i + 1);
          if (Object.prototype.hasOwnProperty.call(overrides, i)) {
            if (v[key] !== overrides[i]) return false;
          } else if (currentVariant && v[key] !== currentVariant[key]) {
            return false;
          }
        }
        return true;
      });
      if (exact) return exact;
      return product.variants.find((v) => {
        return Object.keys(overrides).every((i) => v['option' + (Number(i) + 1)] === overrides[i]);
      });
    }

    // Strict combo check (no fallback) -- used purely to decide whether a
    // given size button should render as available/disabled for whichever
    // color is currently selected.
    variantExistsAndAvailable(product, overrides) {
      const numOptions = product.options.length;
      const v = product.variants.find((variant) => {
        for (let i = 0; i < numOptions; i++) {
          const key = 'option' + (i + 1);
          if (Object.prototype.hasOwnProperty.call(overrides, i) && variant[key] !== overrides[i]) return false;
        }
        return true;
      });
      return !!(v && v.available);
    }

    async renderProduct(url) {
      this.renderLoading();
      let product;
      try {
        // Product URLs coming from Shopify's own search results (this
        // theme's search drawer links product cards via "item.url" off
        // "search.results") carry search-analytics tracking query params
        // (e.g. "?pr_prod_strat=...&pr_rec_pid=...") appended by Shopify's
        // Search & Discovery app -- appending ".js" naively after those
        // params produced "/products/origin?pr_prod_strat=...js", which
        // Shopify's router doesn't recognize as the JSON endpoint and falls
        // back to serving the full storefront HTML page instead (confirmed
        // live: fetch() succeeded with a 200, but the body was an entire
        // <!doctype html> document, not JSON, so "product = await
        // res.json()" always threw and every search-result Quick View
        // silently failed). Strip any query string before adding ".js" so
        // this keeps working regardless of where the trigger's URL came
        // from (product card grids never had this problem since their
        // "product.url" is always clean).
        const cleanUrl = url.split('?')[0];
        const res = await fetch(cleanUrl + '.js');
        if (!res.ok) throw new Error('Product fetch failed');
        product = await res.json();
      } catch (err) {
        this.renderError();
        return;
      }

      this.product = product;
      const images = product.images && product.images.length ? product.images : [product.featured_image].filter(Boolean);
      const colorIndex = this.findColorOptionIndex(product);
      const sizeIndex = this.findSizeOptionIndex(product);
      let selectedVariant = product.variants.find((v) => v.available) || product.variants[0];

      const galleryHtml =
        '<div class="quick-view-drawer__gallery">' +
        '<div class="swiper quick-view-drawer__swiper"><div class="swiper-wrapper">' +
        images
          .map(
            (src) =>
              '<div class="swiper-slide quick-view-drawer__slide"><img src="' +
              src.replace(/(\.[a-z]+)(\?|$)/i, '_800x$1$2') +
              '" loading="lazy" alt=""></div>'
          )
          .join('') +
        '</div></div>' +
        (images.length > 1
          ? '<button type="button" class="quick-view-drawer__arrow quick-view-drawer__arrow--prev" aria-label="' +
            ((window.quickViewStrings && window.quickViewStrings.previous) || 'Previous') +
            '"><svg width="9" height="14" viewBox="0 0 9 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7.5 1L1.5 7L7.5 13" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"></path></svg></button>' +
            '<button type="button" class="quick-view-drawer__arrow quick-view-drawer__arrow--next" aria-label="' +
            ((window.quickViewStrings && window.quickViewStrings.next) || 'Next') +
            '"><svg width="9" height="14" viewBox="0 0 9 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1.5 1L7.5 7L1.5 13" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"></path></svg></button>' +
            '<div class="quick-view-drawer__dots">' +
            images.map((_, i) => '<button type="button" class="quick-view-drawer__dot' + (i === 0 ? ' is-active' : '') + '" data-slide-index="' + i + '"></button>').join('') +
            '</div>'
          : '') +
        '</div>';

      let swatchesHtml = '';
      if (colorIndex > -1) {
        const optionKey = 'option' + (colorIndex + 1);
        const seen = {};
        const swatchVariants = product.variants.filter((v) => {
          if (seen[v[optionKey]]) return false;
          seen[v[optionKey]] = true;
          return true;
        });
        swatchesHtml =
          '<div class="quick-view-drawer__option">' +
          '<span class="quick-view-drawer__option-label">' +
          this.optionNames(product)[colorIndex] +
          ': <span data-quick-view-selected-color>' +
          selectedVariant['option' + (colorIndex + 1)] +
          '</span></span>' +
          '<div class="quick-view-drawer__swatch-list">' +
          swatchVariants
            .map((v) => {
              const swatchImg = v.featured_image ? v.featured_image.src : product.featured_image;
              const isSelected = v[optionKey] === selectedVariant[optionKey];
              return (
                '<button type="button" class="quick-view-drawer__swatch' +
                (isSelected ? ' is-selected' : '') +
                '"' +
                (v.available ? '' : ' disabled') +
                ' data-color-value="' +
                v[optionKey].replace(/"/g, '&quot;') +
                '" aria-label="' +
                v[optionKey].replace(/"/g, '&quot;') +
                '"><img src="' +
                (swatchImg ? swatchImg.replace(/(\.[a-z]+)(\?|$)/i, '_200x$1$2') : '') +
                '" loading="lazy" alt=""></button>'
              );
            })
            .join('') +
          '</div>' +
          '</div>';
      }

      let sizeHtml = '';
      if (sizeIndex > -1) {
        const optionKey = 'option' + (sizeIndex + 1);
        const colorOptionKey = colorIndex > -1 ? 'option' + (colorIndex + 1) : null;
        const seen = {};
        const sizeValues = [];
        product.variants.forEach((v) => {
          if (seen[v[optionKey]]) return;
          seen[v[optionKey]] = true;
          sizeValues.push(v[optionKey]);
        });
        sizeHtml =
          '<div class="quick-view-drawer__option">' +
          '<span class="quick-view-drawer__option-label">' +
          this.optionNames(product)[sizeIndex] +
          '</span>' +
          '<div class="quick-view-drawer__size-list">' +
          sizeValues
            .map((val) => {
              const overrides = { [sizeIndex]: val };
              if (colorOptionKey) overrides[colorIndex] = selectedVariant[colorOptionKey];
              const isAvailable = this.variantExistsAndAvailable(product, overrides);
              const isSelected = selectedVariant[optionKey] === val;
              return (
                '<button type="button" class="quick-view-drawer__size' +
                (isSelected ? ' is-selected' : '') +
                (isAvailable ? '' : ' is-unavailable') +
                '"' +
                (isAvailable ? '' : ' disabled') +
                ' data-size-value="' +
                val.replace(/"/g, '&quot;') +
                '">' +
                val +
                '</button>'
              );
            })
            .join('') +
          '</div>' +
          '</div>' +
          '<a href="#" class="quick-view-drawer__size-guide">' +
          ((window.quickViewStrings && window.quickViewStrings.sizeGuide) || 'Size guide') +
          '</a>';
      }

      const infoHtml =
        '<div class="quick-view-drawer__info">' +
        '<h3 class="quick-view-drawer__title h3">' + product.title + '</h3>' +
        '<div class="quick-view-drawer__price" data-quick-view-price>' +
        this.priceHtml(selectedVariant) +
        '</div>' +
        '<div class="quick-view-drawer__availability' +
        (selectedVariant.available ? '' : ' is-unavailable') +
        '" data-quick-view-availability>' +
        (selectedVariant.available
          ? (window.quickViewStrings && window.quickViewStrings.inStock) || 'In stock, ready to ship'
          : (window.quickViewStrings && window.quickViewStrings.soldOut) || 'Sold out') +
        '</div>' +
        swatchesHtml +
        sizeHtml +
        '<div class="quick-view-drawer__actions">' +
        '<button type="button" class="button button--tertiary quick-view-drawer__add-to-cart" data-quick-view-add-to-cart' +
        (selectedVariant.available ? '' : ' disabled') +
        '><span>' +
        ((window.quickViewStrings && window.quickViewStrings.addToCart) || 'Add to cart') +
        '</span></button>' +
        '<a class="quick-view-drawer__view-full" href="' + product.url + '">' +
        ((window.quickViewStrings && window.quickViewStrings.viewFull) || 'View full details') +
        '</a>' +
        '</div>' +
        '</div>';

      this.content.innerHTML = galleryHtml + infoHtml;
      const headerTitle = this.querySelector('#QuickViewDrawer-Title');
      if (headerTitle) headerTitle.textContent = product.title;
      this.initGallery(images);
      this.bindOptions(product, colorIndex, sizeIndex, images, selectedVariant);
      this.bindSizeGuide();
      this.bindAddToCart(product);
    }

    priceHtml(variant) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        return (
          '<span class="price-item price-item--regular-strike">' +
          this.money(variant.compare_at_price) +
          '</span><span class="price-item price-item--sale">' +
          this.money(variant.price) +
          '</span>'
        );
      }
      return '<span class="price-item price-item--regular">' + this.money(variant.price) + '</span>';
    }

    initGallery(images) {
      const track = this.content.querySelector('.quick-view-drawer__swiper');
      const gallery = this.content.querySelector('.quick-view-drawer__gallery');
      if (!track || images.length < 2 || typeof Swiper === 'undefined') return;

      const prevEl = this.content.querySelector('.quick-view-drawer__arrow--prev');
      const nextEl = this.content.querySelector('.quick-view-drawer__arrow--next');
      const dots = Array.from(this.content.querySelectorAll('.quick-view-drawer__dot'));

      this.swiper = new Swiper(track, {
        slidesPerView: 1,
        navigation: { nextEl: nextEl, prevEl: prevEl },
      });

      const updateDots = () => {
        dots.forEach((dot, i) => dot.classList.toggle('is-active', i === this.swiper.activeIndex));
        gallery.classList.toggle('is-beginning', this.swiper.isBeginning);
        gallery.classList.toggle('is-end', this.swiper.isEnd);
      };
      this.swiper.on('slideChange', updateDots);
      updateDots();

      dots.forEach((dot) => {
        dot.addEventListener('click', () => this.swiper.slideTo(parseInt(dot.dataset.slideIndex, 10)));
      });
    }

    // Shared handler for both color swatches and size buttons -- whichever
    // one the shopper clicks, we resolve the full variant for the new
    // combination, then refresh every piece of UI that depends on it
    // (price, availability, gallery image, the *other* option's button
    // states, and the Add to cart button) from one place, using
    // "this.selectedVariant" as the single source of truth instead of
    // separate closures per option.
    bindOptions(product, colorIndex, sizeIndex, images, initialVariant) {
      this.selectedVariant = initialVariant;
      const colorOptionKey = colorIndex > -1 ? 'option' + (colorIndex + 1) : null;
      const sizeOptionKey = sizeIndex > -1 ? 'option' + (sizeIndex + 1) : null;

      const swatches = Array.from(this.content.querySelectorAll('.quick-view-drawer__swatch'));
      const sizeButtons = Array.from(this.content.querySelectorAll('.quick-view-drawer__size'));

      const refreshSizeButtonStates = () => {
        if (!sizeOptionKey) return;
        sizeButtons.forEach((btn) => {
          const val = btn.dataset.sizeValue;
          const overrides = { [sizeIndex]: val };
          if (colorOptionKey) overrides[colorIndex] = this.selectedVariant[colorOptionKey];
          const isAvailable = this.variantExistsAndAvailable(product, overrides);
          btn.classList.toggle('is-selected', this.selectedVariant[sizeOptionKey] === val);
          btn.classList.toggle('is-unavailable', !isAvailable);
          btn.disabled = !isAvailable;
        });
      };

      const applyVariant = (variant) => {
        if (!variant) return;
        this.selectedVariant = variant;

        if (colorOptionKey) {
          swatches.forEach((s) => s.classList.toggle('is-selected', s.dataset.colorValue === variant[colorOptionKey]));
          const selectedLabel = this.content.querySelector('[data-quick-view-selected-color]');
          if (selectedLabel) selectedLabel.textContent = variant[colorOptionKey];
        }

        refreshSizeButtonStates();

        const priceEl = this.content.querySelector('[data-quick-view-price]');
        if (priceEl) priceEl.innerHTML = this.priceHtml(variant);

        const availEl = this.content.querySelector('[data-quick-view-availability]');
        if (availEl) {
          availEl.classList.toggle('is-unavailable', !variant.available);
          availEl.textContent = variant.available
            ? (window.quickViewStrings && window.quickViewStrings.inStock) || 'In stock, ready to ship'
            : (window.quickViewStrings && window.quickViewStrings.soldOut) || 'Sold out';
        }

        const addToCartBtn = this.content.querySelector('[data-quick-view-add-to-cart]');
        if (addToCartBtn) {
          addToCartBtn.disabled = !variant.available;
          addToCartBtn.classList.remove('is-added', 'is-error');
          const label = addToCartBtn.querySelector('span');
          if (label) label.textContent = (window.quickViewStrings && window.quickViewStrings.addToCart) || 'Add to cart';
        }

        if (variant.featured_image) {
          const imgSrc = this.normalizeSrc(variant.featured_image.src);
          const idx = images.findIndex((src) => this.normalizeSrc(src) === imgSrc);
          if (idx > -1) {
            if (this.swiper) {
              this.swiper.slideTo(idx);
            } else {
              // Single-image gallery (no Swiper instance) -- just swap the
              // <img> src directly so the cover still updates.
              const img = this.content.querySelector('.quick-view-drawer__slide img');
              if (img) img.src = images[idx].replace(/(\.[a-z]+)(\?|$)/i, '_800x$1$2');
            }
          }
        }
      };

      swatches.forEach((swatch) => {
        swatch.addEventListener('click', () => {
          const variant = this.findVariantForOptions(product, { [colorIndex]: swatch.dataset.colorValue }, this.selectedVariant);
          applyVariant(variant);
        });
      });

      sizeButtons.forEach((btn) => {
        btn.addEventListener('click', () => {
          const variant = this.findVariantForOptions(product, { [sizeIndex]: btn.dataset.sizeValue }, this.selectedVariant);
          applyVariant(variant);
        });
      });
    }

    bindSizeGuide() {
      const link = this.content.querySelector('.quick-view-drawer__size-guide');
      if (!link) return;
      // No size-guide modal exists in this theme yet -- keep the link
      // purely visual (matching the reference layout) rather than
      // navigating anywhere or 404-ing.
      link.addEventListener('click', (evt) => evt.preventDefault());
    }

    bindAddToCart(product) {
      const button = this.content.querySelector('[data-quick-view-add-to-cart]');
      if (!button) return;
      button.addEventListener('click', () => this.addToCart(button));
    }

    async addToCart(button) {
      const variant = this.selectedVariant || (this.product && this.product.variants[0]);
      if (!variant || !variant.available || button.getAttribute('aria-disabled') === 'true') return;

      const label = button.querySelector('span');
      const defaultLabel = (window.quickViewStrings && window.quickViewStrings.addToCart) || 'Add to cart';
      button.setAttribute('aria-disabled', 'true');
      button.classList.add('loading');
      button.classList.remove('is-added', 'is-error');

      const cartDrawer = document.querySelector('cart-drawer');
      const body = new FormData();
      body.append('id', variant.id);
      body.append('quantity', 1);
      if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
        body.append('sections', cartDrawer.getSectionsToRender().map((section) => section.id));
        body.append('sections_url', window.location.pathname);
      }

      const config = typeof fetchConfig === 'function' ? fetchConfig('javascript') : { method: 'POST', headers: {} };
      delete config.headers['Content-Type'];
      config.body = body;

      try {
        const res = await fetch(window.routes ? window.routes.cart_add_url : '/cart/add.js', config);
        const data = await res.json();
        if (!res.ok || data.status) {
          throw new Error((data && data.description) || 'Add to cart failed');
        }

        if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
          cartDrawer.renderContents(data);
          // renderContents() always flips on "notification-open" -- this
          // theme's cart-drawer doubles as Shopify's mini "just added this
          // one item" notification popup, which hides every OTHER line via
          // CSS (".notification.notification-open .cart-item{display:none}"
          // / "...cart-item.active{display:block}"). The shopper wants the
          // regular full-cart view (every item in the cart, not only the
          // one just added), so drop back out of notification mode right
          // away instead of leaving the single-item popup showing.
          const cartDrawerDiv = document.getElementById('CartDrawer');
          if (cartDrawerDiv) cartDrawerDiv.classList.remove('notification-open');
          // Both drawers share the same fixed-right ".drawer" positioning
          // and z-index, and quick-view-drawer sits later in the DOM, so it
          // would otherwise paint over the freshly-opened cart drawer and
          // hide the confirmation from the shopper. Close Quick View so the
          // cart drawer (which renderContents() just opened) is visible.
          this.close();
          // When Quick View was opened from a still-open search drawer
          // (search-drawer.js's product-card grid also wires up
          // ".js-quick-view-trigger"), that drawer never closes itself just
          // because Quick View opened on top of it -- confirmed live: after
          // adding to cart, cart-drawer WAS actually opening ("active"
          // class present), but search-drawer sits later in the DOM than
          // cart-drawer (theme.liquid renders cart-drawer, then
          // search-drawer, then this quick-view-modal), so the still-open
          // search drawer painted over the freshly-opened cart drawer and
          // hid it completely -- looked like "add to cart" silently did
          // nothing. Close it too, the same way Quick View closes itself
          // above.
          const searchDrawer = document.querySelector('search-drawer');
          if (searchDrawer && typeof searchDrawer.close === 'function') searchDrawer.close();
        } else {
          window.location = (window.routes && window.routes.cart_url) || '/cart';
          return;
        }

        // cart-drawer's own getSectionsToRender() only ever requests the
        // "cart-drawer" section, so the header's cart-icon-bubble count
        // (a separate, un-related DOM node rendered inline in
        // sections/header.liquid, outside the drawer entirely) never gets
        // refreshed by renderContents() above -- it was stuck at whatever
        // count was present on last full page load until now. Patch it
        // directly here from a fresh /cart.js read instead of trying to
        // route it through the section-rendering machinery.
        try {
          const cart = await fetch('/cart.js').then((r) => r.json());
          const cartLink = document.getElementById('cart-icon-bubble');
          if (cartLink) {
            let countEl = cartLink.querySelector('.header__cart-count');
            if (cart.item_count > 0) {
              if (!countEl) {
                countEl = document.createElement('span');
                countEl.className = 'header__cart-count';
                cartLink.appendChild(countEl);
              }
              countEl.textContent = cart.item_count;
            } else if (countEl) {
              countEl.remove();
            }
          }
        } catch (e) {
          console.error('Failed to refresh cart icon count:', e);
        }

        if (label) label.textContent = (window.quickViewStrings && window.quickViewStrings.addedToCart) || 'Added to cart';
        button.classList.add('is-added');
        setTimeout(() => {
          button.classList.remove('is-added');
          if (label) label.textContent = defaultLabel;
        }, 2000);
      } catch (err) {
        if (label) label.textContent = (window.quickViewStrings && window.quickViewStrings.addToCartError) || "Couldn't add this item to your cart";
        button.classList.add('is-error');
        setTimeout(() => {
          button.classList.remove('is-error');
          if (label) label.textContent = defaultLabel;
        }, 2500);
      } finally {
        button.classList.remove('loading');
        button.removeAttribute('aria-disabled');
      }
    }
  }

  if (!customElements.get('quick-view-drawer')) {
    customElements.define('quick-view-drawer', QuickViewDrawer);
  }

  function initTriggers() {
    document.querySelectorAll('.js-quick-view-trigger').forEach((trigger) => {
      if (trigger.dataset.quickViewBound) return;
      trigger.dataset.quickViewBound = 'true';
      trigger.addEventListener('click', (event) => {
        // Let modifier-clicks / middle-clicks behave normally (open in new tab etc.)
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) return;
        const url = trigger.dataset.productUrl;
        if (!url) return;
        event.preventDefault();
        const drawer = document.querySelector('quick-view-drawer');
        if (!drawer) return;
        // Quick View can be triggered from inside another already-open
        // drawer (Shop The Look on the Lookbook page, the search drawer's
        // results grid, a collection filter drawer, etc). Only one drawer
        // should ever be visible at once, so close whichever one is
        // currently open before Quick View slides in -- otherwise the
        // previous drawer stays visible underneath/behind it.
        document.querySelectorAll('.drawer.active').forEach((openDrawer) => {
          if (openDrawer === drawer) return;
          if (typeof openDrawer.close === 'function') openDrawer.close();
        });
        drawer.open(trigger);
        drawer.renderProduct(url);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initTriggers);
  } else {
    initTriggers();
  }
  document.addEventListener('shopify:section:load', initTriggers);

  // Exposed so search-drawer.js can re-bind Quick View triggers on result
  // cards injected via a raw innerHTML swap -- already idempotent (skips
  // anything with "quickViewBound" set).
  window.initQuickViewTriggers = initTriggers;
})();
